import { css } from '@emotion/css';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Redirect, useHistory, useParams } from 'react-router-dom';
import { RootState } from '../../../features';
import { replaceAllSpaces } from '../../../lib/utils';
import { User } from '../../../models/User';
import { NormalButton } from '../../shared/buttons/NormalButton';
import { CharacterImage } from '../../shared/CharacterImage';
import { reveal } from '../../shared/keyframe-animations';
import { ErrorText } from './ErrorText';

interface DreamHistoryDetailMenuProps {
  uploadUser?: User;
}

export const DreamHistoryDetailMenu: React.FC<DreamHistoryDetailMenuProps> = ({ uploadUser }) => {
  const { t } = useTranslation(['dream', 'translation', 'best_friends']);
  const history = useHistory();
  const params = useParams<{ address?: string }>();
  const { historyDetail } = useSelector((state: RootState) => state.dream);

  const handleDeleteClick = () => {
    if (params?.address) {
      const { address } = params;
      history.replace(`/players/dream/history/detail/${address}/menu/2`);
    }
  };

  const handleReportClick = () => {
    if (params?.address) {
      const { address } = params;
      history.replace(`/players/dream/history/report/${address}`);
    }
  };

  const handleCancelClick = () => {
    if (params?.address) {
      const { address } = params;
      history.replace(`/players/dream/history/detail/${address}`);
    }
  };

  if (!historyDetail.userProfile) {
    return <Redirect to="/players/dream/history" />;
  }

  return (
    <div
      className={css`
        display: flex;
        flex-direction: column;
        width: 100%;
        height: 100%;
        text-align: center;
        background: #f5f1e3;
        .user {
          flex: 1 1 auto;
          min-height: 80px;
          font-size: 14px;
          color: #4d3906;
        }
        .image {
          width: 48px;
          height: 48px;
          margin: 0 auto;
        }
        .name {
          padding: 8px 0;
        }
        .buttons {
          flex: 0 0 auto;
        }
        .button {
          width: 100%;
          opacity: 0;
          visibility: hidden;
          transform: scale(0.9);
          margin: 8px 0 0;
          animation: 0.2s cubic-bezier(0.3, 0.82, 0.54, 1.23) ${reveal} both;
        }
      `}
    >
      <div className="user">
        {uploadUser && (
          <>
            <div className="image">
              <CharacterImage
                src={uploadUser.image}
                isPlayer={true}
                isReported={uploadUser.screening === 'ng'}
                imageAriaLabel={t('best_friends:aria_player_image')}
              />
            </div>
            <p className="name">
              {uploadUser.screening === 'ng'
                ? t('translation:reported_player')
                : replaceAllSpaces(uploadUser.name)}
            </p>
          </>
        )}
      </div>
      <div className="buttons">
        <div className="button">
          <NormalButton onClick={handleDeleteClick}>
            {t('button_delete_dream_history')}
          </NormalButton>
        </div>
        <div className="button">
          <NormalButton
            onClick={handleReportClick}
            disabled={historyDetail.userProfile?.state !== 'ok'}
          >
            {t('translation:button_report')}
          </NormalButton>
        </div>
        <div className="button">
          <NormalButton onClick={handleCancelClick}>
            {t('translation:dialog_button_cancel')}
          </NormalButton>
        </div>
      </div>

      {historyDetail.userProfile.state === 'error' && (
        <ErrorText errorType={historyDetail.userProfile.error} />
      )}
    </div>
  );
};
